import type {FC, PropsWithChildren} from 'react';
import clsx from 'clsx';

import {Container, type ContainerSpacing} from '@/modules/foundation/components/container';
import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import type {ImageWeb} from '@/modules/foundation/components/image/types';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';

type Props = PropsWithChildren<{
	heading?: string;
	image?: ImageWeb;
	spacingTop?: ContainerSpacing;
	spacingBottom?: ContainerSpacing;
	imagePlacement?: 'left' | 'right';
	backgroundColor?:
		| 'white'
		| 'dypgronnIndustri'
		| 'lysgronnIndustri'
		| 'oransjeIndustri'
		| 'blaIndustri'
		| 'gronnIndustri';
}>;

export const textWithImageSplitPortableTextOverrides: PortableTextOverrides = {};

export const TextWithImageSplit: FC<Props> = ({
	heading,
	image,
	imagePlacement = 'right',
	spacingTop,
	spacingBottom,
	children,
	backgroundColor = 'white',
}) => {
	let containerClasses = '';
	let headingColor = '';

	switch (backgroundColor) {
		case 'dypgronnIndustri':
			containerClasses = 'bg-dypgronn-industri';
			headingColor = 'text-lysgronn-industri';
			break;
		case 'lysgronnIndustri':
			containerClasses = 'bg-lysgronn-industri';
			headingColor = 'text-dypgronn-industri';
			break;
		case 'oransjeIndustri':
			containerClasses = 'bg-oransje-industri';
			headingColor = 'text-white';
			break;
		case 'blaIndustri':
			containerClasses = 'bg-bla-industri';
			headingColor = 'text-white';
			break;
		case 'gronnIndustri':
			containerClasses = 'bg-gronn-industri';
			headingColor = 'text-white';
			break;
		default:
			containerClasses = 'bg-white';
			headingColor = 'text-gronn-industri';
			break;
	}

	return (
		<Container spacingTop={spacingTop} spacingBottom={spacingBottom} width="screen">
			{image && (
				<div className="mt-2 md:hidden">
					<ImageBlock
						src={image.image.src}
						width={image.image.width}
						height={image.image.height}
						alt={image.altText ?? ''}
					/>
				</div>
			)}
			{/* New */}
			<Container
				spacingBottom="none"
				spacingTop="none"
				className={clsx(containerClasses, 'md:mt-2')}
			>
				<div>
					<div className={clsx('grid', 'md:grid-cols-11')}>
						<div
							className={clsx(
								'md:col-span-5',
								'md:row-start-1',
								imagePlacement === 'left' ? 'md:col-start-1' : 'md:col-start-7',
								'items-center',
								'hidden',
								'md:flex',
							)}
						>
							{image && (
								<div className={clsx('border-white', 'md:border-x-8', 'w-full')}>
									<ImageBlock
										src={image.image.src}
										width={image.image.width}
										height={image.image.height}
										alt={image.altText ?? ''}
									/>
								</div>
							)}
						</div>
						<div
							className={clsx(
								'md:col-span-6',
								'md:row-start-1',
								imagePlacement === 'left' ? 'md:col-start-6' : 'md:col-start-1',
								'py-12',
								'flex',
								'flex-col',
								'justify-center',
								'md:max-w-[512px]',
								imagePlacement === 'left' ? 'md:ml-20' : 'md:mr-20',
							)}
						>
							<Heading spacing className={headingColor}>
								{heading}
							</Heading>
							{children && <div>{children}</div>}
						</div>
					</div>
				</div>
			</Container>
		</Container>
	);
};
